import { ChangeDetectionStrategy, Component, inject, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, ConfigService, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCalendar, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import moment, { HTML5_FMT } from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateDatePerformed,
    updateFunctionalElementRemarks,
    updateFunctionalElementSelection,
} from '../../store/actions/functional-elements.actions';
import {
    getFormCheckpointByXmlIdFactory,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
    getOptionPresentNoSelectedForFunctionalElement,
    isCosimaFunctionalElementFromTrainingChecklistReadOnly,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentId } from '../../types/content-block';
import { DatePerformed } from '../../types/date-performed';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import { isAstoriaFunctionalElementVisible, isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';

@Component({
    selector: 'pace-forms-date-performed',
    templateUrl: './date-performed.component.html',
    styleUrls: ['./date-performed.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePerformedComponent implements OnInit, OnDestroy {
    private readonly configService = inject(ConfigService);
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public readOnly$: Observable<boolean>;
    public datePerformed$: Observable<DatePerformed>;
    public controllingCheckpoint$: Observable<Checkpoint | undefined>;
    public options$: Observable<RadioOption[] | undefined>;
    public selectedParticipant$: Observable<ParticipantCopyId>;

    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public elementTypeLabel$: Observable<string>;
    public visible$: Observable<boolean>;
    public showRemarksField$: Observable<boolean>;
    public showNotApplicableCheckbox$: Observable<boolean>;
    public optionPresentNoSelected$: Observable<boolean>;
    public displayHistoryButton$: Observable<boolean>;

    private destroy$ = new Subject<void>();

    public datePerformedGroup: FormGroup;
    public dateInputControl: FormControl;
    public checkboxControl: FormControl;
    public radioGroup: FormGroup;
    public radioControl: FormControl;
    public remarksControl: FormControl;
    public faCalendar = faCalendar;
    public faCheck = faCheck;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
    ) {
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
    }

    public ngOnInit(): void {
        this.radioControl = this.formBuilder.control('');
        this.remarksControl = this.formBuilder.control('');
        this.radioGroup = this.formBuilder.group({ radioControl: this.radioControl });

        this.datePerformedGroup = this.formBuilder.group({
            remarks: this.remarksControl,
            radioGroup: this.radioGroup,
        });

        this.dateInputControl = this.formBuilder.control('');
        this.checkboxControl = this.formBuilder.control('');
        this.datePerformedGroup.addControl('date', this.dateInputControl);
        this.datePerformedGroup.addControl('date', this.checkboxControl);

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.datePerformedGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'DatePerformedComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }

        const optionPresentNoSelectedSelector = getOptionPresentNoSelectedForFunctionalElement({ contentId: this.id });
        this.optionPresentNoSelected$ = this.store.select(optionPresentNoSelectedSelector);

        this.datePerformed$ = this.store
            .select(getFormFunctionalElementByIdFactory<DatePerformed>({ id: this.id }))
            .pipe(filter(isDefined));

        this.displayHistoryButton$ = this.datePerformed$.pipe(
            map(
                (datePerformed) =>
                    isDefined(datePerformed.uniqueId) &&
                    datePerformed.uniqueId.length > 0 &&
                    this.configService.getEnableFormsHistoricalValues(),
            ),
        );

        this.readOnly$ = combineLatest([
            this.store.select(getFormParticipantCopyReadonly),
            this.store.select(isCosimaFunctionalElementFromTrainingChecklistReadOnly({ id: this.id })),
        ]).pipe(
            map(([readOnly, isCosimaReadOnly]) => {
                const returnValue = readOnly || isCosimaReadOnly;
                return returnValue;
            }),
        );

        this.elementTypeLabel$ = combineLatest([
            this.datePerformed$.pipe(
                map((d) => d.elementType),
                filter(isDefined),
            ),
            this.store.select(getFormTypeFilters),
        ]).pipe(map(([elementType, filters]) => FormUtilities.getFilterLabel(elementType, filters)));

        this.controllingCheckpoint$ = this.datePerformed$.pipe(
            mergeMap((datePerformed) =>
                datePerformed.controllingCheckpoint.length > 0
                    ? this.store
                          .select(getFormCheckpointByXmlIdFactory({ xmlId: datePerformed.controllingCheckpoint }))
                          .pipe(take(1))
                    : of(undefined),
            ),
        );

        this.options$ = this.controllingCheckpoint$.pipe(
            switchMap((checkpoint) =>
                typeof checkpoint !== 'undefined'
                    ? this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId }))
                    : of(undefined),
            ),
        );

        this.visible$ = combineLatest([
            this.datePerformed$,
            this.controllingCheckpoint$,
            this.options$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([datePerformed, checkpoint, options, typeFilters, formFilters, selectedParticipant]) => {
                const isMasterCopy = selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID;
                // The  master displays everything. In a participant copy, we display only content that was filled out in
                // the  current session and not loaded from XML.
                let visible = isMasterCopy || !datePerformed.contentLoadedFromXml;

                // choi ces are only available in cosima.
                if (datePerformed.choices) {
                    // in p articipant copies, only content that was answered with "Yes" in this session is displayed
                    const selectedInMaster = Object.values(datePerformed.choices).find((c) => c?.selectedInMaster);
                    const yesInMaster =
                        typeof selectedInMaster !== 'undefined' &&
                        selectedInMaster.type === CHOICE_TYPE.CHOICE_POSITIVE;
                    const visibleInParticipantCopy = !datePerformed.contentLoadedFromXml && yesInMaster;

                    // filt ers apply both to master and participant copies
                    const visibleByFilter = isCosimaFunctionalElementVisible(
                        datePerformed,
                        typeFilters,
                        formFilters,
                        datePerformed.elementType,
                    );

                    visible = (isMasterCopy || visibleInParticipantCopy) && visibleByFilter;
                } else {
                    // Asto ria only!
                    // A co ntrolling checkpoint can control the visibility by either the type
                    // of t he checkpoint and a selected matching filter, or by the chosen answer: In a participant copy, we
                    // disp lay only values answered with "yes".
                    const visibleByFilter = isAstoriaFunctionalElementVisible(
                        datePerformed,
                        checkpoint?.elementType,
                        typeFilters,
                        formFilters,
                        checkpoint?.xmlId ? { [checkpoint.xmlId]: options } : {},
                    );
                    visible = visibleByFilter && visible;
                    if (typeof checkpoint !== 'undefined' && typeof options !== 'undefined') {
                        const selectedInMaster = options.find((o) => o.selectedInMaster);
                        const yesInMaster =
                            typeof selectedInMaster !== 'undefined' &&
                            selectedInMaster.optionType === RADIO_OPTION_TYPE.YES &&
                            !selectedInMaster.contentLoadedFromXml;
                        visible =
                            visible &&
                            // The  master displays everything. Participant copies only show content that was not loaded from
                            // an i nterim XML and that was answered with YES.
                            (isMasterCopy || yesInMaster);
                    }
                }

                return visible;
            }),
        );

        combineLatest([this.datePerformed$, this.readOnly$])
            .pipe(withLatestFrom(this.optionPresentNoSelected$), takeUntil(this.destroy$))
            .subscribe(([[datePerformed, readOnly], optionPresentNoSelected]) => {
                const date =
                    datePerformed.date.length > 0 && datePerformed.date !== FormsConstants.NA
                        ? moment(datePerformed.date)
                        : moment.invalid();
                // we a lways need to emit an event for the dateInputControl, if the choices for cosima are changing.
                this.dateInputControl.patchValue(date.isValid() ? date.format(HTML5_FMT.DATE) : '');
                this.dateInputControl[datePerformed.disabled || readOnly ? 'disable' : 'enable']({ emitEvent: false });

                this.checkboxControl[readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });

                // cosi ma choices
                if (datePerformed.choices) {
                    const hasEnteredData =
                        datePerformed.date.length > 0 || (datePerformed.remarks && datePerformed.remarks.length > 0);
                    const selectedChoice = Object.values(datePerformed.choices).find((choice) => choice?.selected);

                    if (isDefined(selectedChoice)) {
                        this.radioControl.patchValue(selectedChoice.xmlId);
                    } else if (!hasEnteredData) {
                        this.radioControl.reset('');
                    }

                    this.radioControl[optionPresentNoSelected || readOnly ? 'disable' : 'enable']({
                        emitEvent: false,
                    });

                    this.remarksControl.patchValue(datePerformed.remarks);
                    this.remarksControl[
                        datePerformed.disabled || readOnly || selectedChoice?.type !== CHOICE_TYPE.CHOICE_NEGATIVE
                            ? 'disable'
                            : 'enable'
                    ]({
                        emitEvent: false,
                    });
                }
            });

        this.showRemarksField$ = this.datePerformed$.pipe(
            map(
                (datePerformed) => !!(datePerformed.remarks?.length || datePerformed.choices?.choiceNegative?.selected),
            ),
        );
        this.showNotApplicableCheckbox$ = this.datePerformed$.pipe(
            map(
                (datePerformed) =>
                    (datePerformed.controllingCheckpoint.length === 0 && !isDefined(datePerformed.choices)) ||
                    (isDefined(datePerformed.choices) && isDefined(datePerformed.function)),
            ),
        );

        this.dateInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of datePerformed$
                withLatestFrom(this.datePerformed$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newVal, datePerformed]: [string, DatePerformed]) => {
                const newDate = moment(newVal).startOf('day');
                const currentDate = moment().startOf('day');

                if (isDefined(datePerformed.function)) {
                    if (
                        (datePerformed.function === FormsConstants.FUNCTION_TYPE.OLDER &&
                            currentDate.isAfter(newDate)) ||
                        (datePerformed.function === FormsConstants.FUNCTION_TYPE.NEWER && currentDate.isBefore(newDate))
                    ) {
                        this.radioControl.setValue(datePerformed.choices?.choiceNegative?.xmlId);
                    } else {
                        if (newDate.isValid()) {
                            this.radioControl.setValue(datePerformed.choices?.choicePositive?.xmlId);
                        } else {
                            this.radioControl.setValue(datePerformed.choices?.choiceNa?.xmlId);
                        }
                    }
                }

                this.store.dispatch(
                    updateDatePerformed({
                        id: datePerformed.id,
                        dayXmlId: datePerformed.dayXmlId,
                        monthXmlId: datePerformed.monthXmlId,
                        yearXmlId: datePerformed.yearXmlId,
                        dateXmlId: datePerformed.dateXmlId,
                        newValue: newDate.isValid() ? newDate.toISOString(true) : '',
                    }),
                );
            });

        this.checkboxControl.valueChanges
            .pipe(
                // the  combination of startWith and pairWise enables us to access the previous and the current value in
                // the  observable
                startWith(false),
                pairwise(),
                // we u se take(1) because we are only interested in static properties of materialSerialNumber$
                withLatestFrom(this.datePerformed$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([[previousChecked, currentChecked], datePerformed]) => {
                // for  cosima only, when the radio should be hidden
                if (isDefined(datePerformed.choices)) {
                    if (currentChecked) {
                        this.radioControl.patchValue(datePerformed.choices.choiceNa?.xmlId);
                    } else {
                        this.radioControl.reset('');
                    }
                } else {
                    if (previousChecked !== currentChecked) {
                        this.store.dispatch(
                            updateDatePerformed({
                                id: datePerformed.id,
                                dayXmlId: datePerformed.dayXmlId,
                                monthXmlId: datePerformed.monthXmlId,
                                yearXmlId: datePerformed.yearXmlId,
                                dateXmlId: datePerformed.dateXmlId,
                                newValue: '',
                            }),
                        );
                    }
                    this.dateInputControl[currentChecked ? 'disable' : 'enable']({ emitEvent: false });
                }
            });

        this.radioControl.valueChanges
            .pipe(distinctUntilChanged(), withLatestFrom(this.datePerformed$), takeUntil(this.destroy$))
            .subscribe(([value, datePerformed]: [string | null, DatePerformed]) => {
                this.store.dispatch(
                    updateFunctionalElementSelection({
                        id: datePerformed.id,

                        selectedChoiceXmlId: value === null ? undefined : value,
                    }),
                );
            });

        this.remarksControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.datePerformed$),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, datePerformed]: [string, DatePerformed]) => {
                if (!isDefined(datePerformed.xmlId)) {
                    throw new Error(
                        `DatePerformedComponent: remarksControl valueChanges: date performed element has no xml Id!`,
                    );
                }
                if (datePerformed.remarksXmlId) {
                    this.store.dispatch(
                        updateFunctionalElementRemarks({
                            id: datePerformed.id,
                            // usin g ! because remarksXmlId is guaranteed to be there in Cosima. It is only undefined in Astoria.
                            xmlId: datePerformed.remarksXmlId,
                            newValue,
                        }),
                    );
                }
            });
    }

    public ngOnDestroy(): void {
        // remo ve form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.datePerformedGroup);
            this.parentForm.removeAt(index);
        }
        this.destroy$.next();
    }

    public setToday(): void {
        this.dateInputControl.setValue(moment().format(HTML5_FMT.DATE));
    }

    public reset(): void {
        this.radioControl.reset();
    }
}
